import type { Particle } from "@infinitaslearning/app-player";
import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";

import { useDiscoContent } from "hooks/useDiscoContent";
import { useReactTooltip } from "hooks/useReactTooltip";

import { BackButton } from "components/BackButton";
import { ImmersiveReaderButton } from "components/ImmersiveReaderButton/ImmersiveReaderButton";
import { InfoMessage } from "components/InfoMessage/InfoMessage";
import { LoadingSpinner } from "components/LoadingSpinner";
import { PlayerParticleHtml } from "components/PlayerParticleHtml";

import { Heading1 } from "styles/elements/Headings";
import { Box, CssFlex } from "styles/helpers/layout";
import { EdifyChapterBox } from "./components/EdifyChapterBox/EdifyChapterBox";

type EdifySubjectDetailsPagePathParams = {
  productId: string;
  packageId: string;
  chapterId: string;
};

export const EdifySubjectDetailsPage: React.FC = observer(() => {
  const { t } = useTranslation("homepage");
  const { productId, packageId, chapterId } = useParams<EdifySubjectDetailsPagePathParams>();
  const { loading: isLoading, edifySubjectDetails, fetchEdifySubjectDetails } = useDiscoContent();

  useReactTooltip(!isLoading && !!edifySubjectDetails);

  useEffect(() => {
    if (packageId && chapterId) {
      fetchEdifySubjectDetails(packageId, chapterId);
    }
  }, [packageId, chapterId, fetchEdifySubjectDetails]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!edifySubjectDetails || !packageId || !chapterId || !productId) {
    return (
      <InfoMessage
        dataCy="no-content-message"
        icon="fas fa-debug"
        title={t("edifySubjectDetails.noContentError.title")}
      >
        {t("edifySubjectDetails.noContentError.description", { packageId, chapterId })}
      </InfoMessage>
    );
  }

  const backButtonRedirectURL = buildUrlWithPathParams(
    PRODUCT_PAGES_ROUTES.PRODUCT_EDIFY_MODULE_DETAILS,
    {
      productId,
      packageId,
    },
  );

  const buildEdifyTopicDetailsUrl = (topicId: string) =>
    buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_EDIFY_TOPIC_DETAILS, {
      productId,
      packageId,
      chapterId,
      contentUnitId: topicId,
    });

  return (
    <Box>
      <BackButton
        dataCy="return-to-edify-module-button"
        label={t("edifySubjectDetails.button.back", "Back")}
        redirectUrl={backButtonRedirectURL}
      />

      {/* -- Subject name and description -- */}
      <Box data-ir-target="edify-subject-heading">
        <Heading1 data-cy="edify-subject-title">
          <ImmersiveReaderButton
            targetIndex={0}
            targetSelector={'[data-ir-target="edify-subject-heading"]'}
          />
          {edifySubjectDetails.title}
        </Heading1>
        {edifySubjectDetails.description && (
          <Box data-cy="edify-subject-description" paddingTop="1rem">
            <PlayerParticleHtml particle={edifySubjectDetails.description as unknown as Particle} />
          </Box>
        )}
      </Box>

      {/* -- Subject content overview -- */}
      <CssFlex flexDirection="column" paddingTop="2.5rem">
        {edifySubjectDetails.chapters.map((chapter) => (
          <EdifyChapterBox
            chapter={chapter}
            key={chapter.id}
            buildEdifyTopicDetailsUrl={buildEdifyTopicDetailsUrl}
          />
        ))}
      </CssFlex>
    </Box>
  );
});
